import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    CAApi,
    CACertificateSignatureAlgorithm,
    CAControllerRequestNewCertificateKeyLengthEnum,
    CertificateRequestHandlerDetails,
    PropertyApi,
    X500PrincipalRequest
} from "components/src/api/admin/generated";
import {X500Subject} from "components/src/ca/X500Subject";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {useEffect, useState} from 'react'
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import {useNavigate} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const caApi = new CAApi(ApiConfig.config());
const propertyApi = new PropertyApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface RequestCertificateDialogProps {
    onClose : OnClose
}

export const RequestCertificateDialog = ({onClose} : RequestCertificateDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Certificate subject
     */
    const [subject, setSubject] = useState<X500PrincipalRequest>({commonName : ""});
    /**
     * Key length
     */
    const [keyLength, setKeyLength] = useState<number>(3072);
    /**
     * Validity
     */
    const [validity, setValidity] = useState<number>(3650);
    /**
     * Signature algorithm
     */
    const [signatureAlgorithm, setSignatureAlgorithm] = useState<CACertificateSignatureAlgorithm>(CACertificateSignatureAlgorithm.Sha256WithRsaEncryption);
    /**
     * Request handler for the request
     */
    const [selectedRequestHandler, setSelectedRequestHandler] = useState<string>();
    /**
     * All available request handlers
     */
    const [requestHandlers, setRequestHandlers] = useState<CertificateRequestHandlerDetails[]>([]);
    /**
     * Set to true while requesting the certificates to show a spinner on the import button
     */
    const [requesting, setRequesting] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * Load available request handlers
     */
    useEffect(() =>
    {
        logger.debug("Getting available request handlers");

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        caApi.cAControllerGetCertificateRequestHandlers().then((result) => {
            setRequestHandlers(result.data);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Getting available request handlers", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);


    /**
     * Load default request handler
     */
    useEffect(() =>
    {
        logger.debug("Getting default request handler");

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        propertyApi.propertyControllerGetGlobalProperty("ca-default-certificate-request-handler").then((result) => {
            setSelectedRequestHandler(result.data.value);
        })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting default request handler", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    const requestCertificate = () =>
    {
        setRequesting(true);

        caApi.cAControllerRequestNewCertificate(
                    subject?.email??"",
                    validity,
                    keyLength as CAControllerRequestNewCertificateKeyLengthEnum,
                    signatureAlgorithm,
                    selectedRequestHandler??"",
                    subject).then(() =>
            {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error requesting certificate", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setRequesting(false);
            });
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} fullscreen="lg-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("ca.request-certificate")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">

                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>

                        <Row>
                            <X500Subject header={t("ca.subject")}
                                         subject={subject}
                                         setSubject={setSubject}
                                         keyLength={keyLength}
                                         setKeyLength={setKeyLength}
                                         validity={validity}
                                         setValidity={setValidity}
                            />
                        </Row>
                        <Row>
                            <Form.Group className="mt-3">
                                <Form.Label>{t("ca.signature-algorithm")}</Form.Label>
                                <Form.Select value={signatureAlgorithm}
                                             onChange={(e) => setSignatureAlgorithm(Utils.stringToEnum(CACertificateSignatureAlgorithm, e.target.value as CACertificateSignatureAlgorithm))}>
                                    <option value="SHA256_WITH_RSA_ENCRYPTION">SHA256WithRsa</option>
                                    <option value="SHA384_WITH_RSA_ENCRYPTION">SHA384WithRsa</option>
                                    <option value="SHA512_WITH_RSA_ENCRYPTION">SHA512WithRsa</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mt-3">
                                <Form.Label>{t("ca.request-handler")}</Form.Label>
                                <Form.Select value={selectedRequestHandler}
                                             onChange={(e) => setSelectedRequestHandler(e.target.value)}>
                                    {requestHandlers.map(h => (
                                        <option key={h.name} value={h.name} disabled={!h.enabled}>{h.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Row>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={requestCertificate}
                        disabled={StringUtils.isBlank(subject.email) || StringUtils.isBlank(subject.commonName) ||
                            StringUtils.isBlank(selectedRequestHandler)}>
                    {requesting && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {requesting ? t("ca.requesting") : t("ca.request")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
